import React, {useContext, useState} from "react";
import {createUseStyles} from "react-jss";
import {EngageAppContext} from "../../App";
import {Theme} from "engage-client/theme";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import Button from "engage-client/components/Button";
import {TriviaGameState} from "../../state/state";
import {EngageMessageType} from "engage-common/messages";

const useStyles = createUseStyles<Theme>((theme) => ({
	titleBar: {
		color: "white",
		padding: 10,
		backgroundColor: theme.color.primary.main
	},
	explanation: {
		padding: 16
	},
	questionUpload: {
		height: 500,
		width: 500,
	},
	fileStatus: {
		padding: 16
	},
	issues: {
		overflowY:"auto",
	},
	buttons: {
		position: "absolute",
		bottom: 0,
		left: 0,
		right: 0,
		borderTop: "1px solid gray",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: 16
	},
	button: {
		margin: 16
	}
}));

type StateUploadProps = {
	closeHandler:()=>void
}

const StateUpload = React.forwardRef<HTMLDivElement, StateUploadProps>(({closeHandler}:StateUploadProps, ref) => {
	const classes = useStyles();
	const {sendMessage} = useContext(EngageAppContext.current);
	const [file,setFile] = useState<File>();
	const [fileContents, setFileContents] = useState<string>();
	const [issue, setIssue] = useState<string>();

	function handleChange(files:FileList|null) {
		if(files?.length) {
			const file = files[0];
			loadFromFile(file);
		}
	}

	function loadFromFile(file:File) {
		const fileReader = new FileReader();
		fileReader.onload = () => {
			if(typeof fileReader.result === "string") {
				try {
					const triviaState = JSON.parse(fileReader.result);
					if(triviaState.lobbyState && triviaState.hostLobby && triviaState.serverLobby) {
						//Cursory validation above, we validate with a JSON schema on the server.
						setFileContents(fileReader.result);
						setFile(file);
					} else {
						setIssue("The file did not have a lobbyState and hostLobby property.");
					}
				} catch (e) {
					setIssue("The file is not valid JSON.");
				}
			} else {
				setIssue("The file could not be loaded as a string.");
			}
		};
		setIssue(undefined);
		setFile(undefined);
		setFileContents(undefined);
		fileReader.readAsText(file);
	}

	function handleSubmit() {
		if(fileContents) {
			sendMessage({
				type: EngageMessageType.SetState,
				file: fileContents
			},(valid:boolean, issues:any) => {
				if(valid) {
					closeHandler();
					//force the host to get an updated state from the server
					window.location.reload();
				} else {
					setIssue("There were issues restoring the state from this file. See the Console for details");
					console.log(issues);
				}
			});
		}
	}

	return (
		<div className={classes.questionUpload} ref={ref}>
			<div className={classes.titleBar}>
				<DisplayText variant={TextVariant.s24reg} fontWeight={600}>Upload Lobby State</DisplayText>
			</div>
			<DisplayText variant={TextVariant.s16reg} className={classes.explanation}>
				Upload a JSON file that was created using the Download State button.
				This allows you to restore the lobby to the state it was when you downloaded.
				However, players will not be able to reconnect as themselves.
				For the most part, this should only be used for testing and automation.
			</DisplayText>
			<input
				color="primary"
				accept="application/json"
				type="file"
				onChange={(e) => {handleChange(e.target.files);}}
				id="state-json-input"
				style={{display: "none"}}
			/>
			<label htmlFor="state-json-input">
				<Button
					className={classes.button}
					component="span"
				>
					Select File
				</Button>
				{file?.name}
			</label>
			{!!issue &&
			<DisplayText variant={TextVariant.s16reg} className={classes.explanation}>{issue}</DisplayText>
			}
			<div className={classes.buttons}>
				<Button onClick={closeHandler}>Cancel</Button>
				<Button color="primary" onClick={handleSubmit}
						disabled={!file || !fileContents || (!!issue && issue.length > 0)}>
					Upload and Set
				</Button>
			</div>
		</div>
	);

});

export default StateUpload;